import { DOCUMENT } from '@angular/common';
import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { WINDOW } from './window.token';

/** Délai entre deux contrôles de version, en millisecondes. */
const POLL_MS = 5 * 60 * 1000;
/** Délai minimal entre deux contrôles, pour qu'un va-et-vient d'onglets n'en déclenche pas une rafale. */
const MIN_GAP_MS = 30 * 1000;
/** Le script principal porte l'empreinte du build : `main-NJENZNAH.js`. */
const MAIN_SCRIPT = /main-[A-Za-z0-9]+\.js/;
/** Empreinte pour laquelle un rechargement a déjà été tenté, dans cet onglet. */
const ATTEMPT_KEY = 'cesmon-build-reload-attempt';

/**
 * Recharge la page quand une nouvelle version du front est en ligne.
 *
 * CloudFront a servi des versions périmées à des postes de test, et la seule
 * issue était un Ctrl+Maj+R manuel. Les en-têtes du CDN sont corrigés, donc un
 * chargement de page rapporte toujours la dernière version. Reste l'onglet
 * laissé ouvert pendant un déploiement : il garde son bundle jusqu'à ce qu'un
 * morceau manquant le fasse échouer, ce que rattrape CesGlobalErrorHandler.
 *
 * Ce service ferme ce trou. Il compare l'empreinte du script principal servi
 * par le serveur à celle que la page exécute, puis recharge à la prochaine
 * navigation réussie. Attendre la navigation évite de perdre une saisie en
 * cours : les gardes de sortie ont déjà fait leur travail à cet instant, et
 * l'écran qui vient de s'afficher ne contient encore rien.
 */
@Injectable({ providedIn: 'root' })
export class BuildWatcherService {
  private readonly window = inject(WINDOW);
  private readonly document = inject(DOCUMENT);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  private readonly running = this.runningBuild();
  private target: string | null = null;
  private lastCheck = 0;
  private checking = false;

  start(): void {
    // Sans empreinte, le serveur de développement sert `main.js` : rien à comparer.
    if (!this.running) return;

    const timer = this.window.setInterval(() => void this.check(), POLL_MS);
    this.destroyRef.onDestroy(() => this.window.clearInterval(timer));

    const onVisible = (): void => {
      if (this.document.visibilityState === 'visible') void this.check();
    };
    this.document.addEventListener('visibilitychange', onVisible);
    this.destroyRef.onDestroy(() => this.document.removeEventListener('visibilitychange', onVisible));

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.reloadOnce());
  }

  /** Empreinte servie par le serveur, ou null si le contrôle n'aboutit pas. */
  async check(): Promise<void> {
    const now = Date.now();
    // Sans empreinte locale, la comparaison n'a pas d'objet : ne rien demander au serveur.
    if (!this.running || this.checking || now - this.lastCheck < MIN_GAP_MS) return;
    this.checking = true;
    this.lastCheck = now;
    try {
      // baseURI, jamais une URL relative : sur /assets/35/monitoring/dashboard elle
      // viserait /assets/35/monitoring/index.html. Le paramètre change la clé d'objet
      // du CDN, donc la réponse vient de l'origine et non d'un bord qui garde la
      // page périmée.
      const url = new URL(`index.html?build-check=${now}`, this.document.baseURI).toString();
      const response = await fetch(url, { cache: 'no-store' });
      if (!response.ok) return;
      const served = MAIN_SCRIPT.exec(await response.text())?.[0] ?? null;
      if (served && served !== this.running) this.target = served;
    } catch {
      // Hors ligne, CDN en vrac, onglet qui se ferme : un contrôle raté ne doit rien casser.
    } finally {
      this.checking = false;
    }
  }

  /** Vrai dès qu'une version plus récente est en ligne. */
  get newBuildPending(): boolean {
    return this.target !== null;
  }

  /**
   * Recharge une seule fois par empreinte servie.
   *
   * Si le navigateur reçoit encore l'ancienne page au rechargement, ce que fait
   * justement un bord de CDN périmé, la comparaison resterait vraie et chaque
   * navigation rechargerait. La marque borne la boucle à une tentative, comme
   * le fait CesGlobalErrorHandler pour un morceau manquant.
   */
  private reloadOnce(): void {
    const wanted = this.target;
    if (!wanted || this.readAttempt() === wanted) return;
    this.writeAttempt(wanted);
    this.window.location.reload();
  }

  private readAttempt(): string | null {
    try {
      return sessionStorage.getItem(ATTEMPT_KEY);
    } catch {
      return null;
    }
  }

  private writeAttempt(value: string): void {
    try {
      sessionStorage.setItem(ATTEMPT_KEY, value);
    } catch {
      // Navigation privée ou stockage refusé : on perd la borne, pas le rechargement.
    }
  }

  private runningBuild(): string | null {
    const scripts = Array.from(this.document.querySelectorAll('script[src]'));
    for (const script of scripts) {
      const found = MAIN_SCRIPT.exec(script.getAttribute('src') ?? '');
      if (found) return found[0];
    }
    return null;
  }
}
